import { createAnimation, type Animation } from "@ionic/vue"
import type { TransitionOptions } from "@ionic/core/components"

/**
 * Custom page transition for IonRouterOutlet.
 *
 * The default Ionic transition lets the leaving page stay sharp under the
 * entering page, which made route changes feel like they were bleeding into
 * each other. This animation:
 *
 *  - Slides the entering page in from the side (right on forward, left on
 *    back), fading from opacity 0 to 1.
 *  - Pushes the leaving page down a touch, drops its opacity, and applies a
 *    short blur so it visibly recedes behind the new page instead of
 *    competing with it.
 *
 * The animation is registered via `IonicVue({ navAnimation })` so it applies
 * to every router-driven page change.
 */
export function pageTransition(_baseEl: HTMLElement, opts: TransitionOptions): Animation {
  const DURATION = 320
  const isBack = opts.direction === "back"
  const enterFromX = isBack ? "-18%" : "18%"

  const rootAnim = createAnimation()
    .duration(DURATION)
    .easing("cubic-bezier(0.32, 0.72, 0, 1)")

  // Entering page slides in fully opaque. Fading it would let the leaving
  // page bleed through where the two overlap — which is exactly the issue
  // the .ion-page background fix is trying to eliminate.
  const enterAnim = createAnimation()
    .addElement(opts.enteringEl)
    .beforeStyles({ "z-index": "101", "will-change": "transform" })
    .fromTo("transform", `translate3d(${enterFromX}, 0, 0)`, "translate3d(0, 0, 0)")
    .afterClearStyles([ "transform", "z-index", "will-change" ])

  rootAnim.addAnimation(enterAnim)

  if (opts.leavingEl) {
    const leaveAnim = createAnimation()
      .addElement(opts.leavingEl)
      .beforeStyles({ "z-index": "100", "will-change": "transform, opacity, filter" })
      .fromTo("transform", "translate3d(0, 0, 0) scale(1)", "translate3d(0, 0, 0) scale(0.96)")
      .fromTo("opacity", "1", "0.35")
      .fromTo("filter", "blur(0px)", "blur(6px)")
      .afterClearStyles([ "transform", "opacity", "filter", "z-index", "will-change" ])

    rootAnim.addAnimation(leaveAnim)
  }

  return rootAnim
}
